import React, { useRef, useEffect, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Type,
  Palette,
  Quote,
  Code
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing...',
  className = ''
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Prevent unnecessary updates to avoid cursor jump
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateValue();
  };

  const updateValue = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text =
      e.clipboardData.getData('text/html') ||
      e.clipboardData.getData('text/plain');

    // Clean and format the pasted content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;

    // Remove unwanted tags
    const unwantedTags = ['script', 'style', 'meta', 'link'];
    unwantedTags.forEach(tag => {
      const elements = tempDiv.getElementsByTagName(tag);
      for (let i = elements.length - 1; i >= 0; i--) {
        elements[i].remove();
      }
    });

    // Clean up attributes
    const allElements = tempDiv.getElementsByTagName('*');
    for (let i = 0; i < allElements.length; i++) {
      const element = allElements[i];
      const allowedAttributes = ['href', 'src', 'alt', 'title', 'class'];
      const attributes = Array.from(element.attributes);
      attributes.forEach(attr => {
        if (!allowedAttributes.includes(attr.name)) {
          element.removeAttribute(attr.name);
        }
      });
    }

    const cleanHtml = tempDiv.innerHTML;
    document.execCommand('insertHTML', false, cleanHtml);
    updateValue();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const changeFontSize = () => {
    const size = prompt('Enter font size (1-7):');
    if (size && /^[1-7]$/.test(size)) {
      execCommand('fontSize', size);
    }
  };

  const changeColor = () => {
    const color = prompt('Enter color (e.g., #ff0000 or red):');
    if (color) {
      execCommand('foreColor', color);
    }
  };

  const insertList = (ordered: boolean = false) => {
    execCommand(ordered ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const insertQuote = () => {
    execCommand('formatBlock', '<blockquote>');
  };

  const insertCode = () => {
    execCommand('formatBlock', '<pre>');
  };

  const toolbarButtons = [
    { icon: <Bold size={16} />, title: 'Bold', action: () => execCommand('bold') },
    { icon: <Italic size={16} />, title: 'Italic', action: () => execCommand('italic') },
    { icon: <Underline size={16} />, title: 'Underline', action: () => execCommand('underline') },
    { separator: true },
    { icon: <Type size={16} />, title: 'Font Size', action: changeFontSize },
    { icon: <Palette size={16} />, title: 'Text Color', action: changeColor },
    { separator: true },
    { icon: <AlignLeft size={16} />, title: 'Align Left', action: () => execCommand('justifyLeft') },
    { icon: <AlignCenter size={16} />, title: 'Align Center', action: () => execCommand('justifyCenter') },
    { icon: <AlignRight size={16} />, title: 'Align Right', action: () => execCommand('justifyRight') },
    { separator: true },
    { icon: <List size={16} />, title: 'Bullet List', action: () => insertList(false) },
    { icon: <ListOrdered size={16} />, title: 'Numbered List', action: () => insertList(true) },
    { separator: true },
    { icon: <LinkIcon size={16} />, title: 'Insert Link', action: insertLink },
    { icon: <Quote size={16} />, title: 'Insert Quote', action: insertQuote },
    { icon: <Code size={16} />, title: 'Insert Code', action: insertCode }
  ];

  return (
    <div className={`border rounded-lg overflow-hidden ${className}`} style={{ borderColor: '#e2e8f0' }}>
      {/* Toolbar */}
      <div 
        style={{ 
          backgroundColor: '#f8fafc', 
          borderBottom: '1px solid #e2e8f0', 
          padding: '8px', 
          display: 'flex', 
          flexDirection: 'row', 
          flexWrap: 'wrap', 
          gap: '4px' 
        }}
      >
        {toolbarButtons.map((button: any, index: number) =>
          button.separator ? (
            <div key={index} style={{ width: 1, height: 24, backgroundColor: '#e2e8f0', margin: '0 4px' }} />
          ) : (
            <button
              key={index}
              type="button"
              onClick={button.action}
              title={button.title}
              style={{
                padding: 8,
                borderRadius: 4,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {button.icon}
            </button>
          )
        )}
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[200px] p-4 focus:outline-none"
        style={{
          minHeight: 200,
          padding: 16,
          outline: 'none',
          direction: 'ltr',
          textAlign: 'left',
          fontSize: 16,
          lineHeight: 1.5,
          color: '#1e293b'
        }}
        onInput={updateValue}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
